// Media Library — pure, presentation-agnostic helpers.
// WL-023 (Fable Hail Mary Wave 2). No React, no I/O — unit-tested in helpers.test.js.
//
// EW-010 honesty note: `usedInWheels` is a refcount the BE owns (media refcount
// lifecycle = WL-072). The FE never mutates it — it only READS it to render
// honest deletion/reuse states (a delete of an in-use asset is surfaced, never
// silent). See refcountLabel / isDeletionDestructive below.

export const MEDIA_TYPES = ["all", "images", "videos", "audio", "documents", "captures"];

export const TYPE_TABS = [
  { key: "all", label: "All" },
  { key: "images", label: "Images" },
  { key: "videos", label: "Videos" },
  { key: "audio", label: "Audio" },
  { key: "documents", label: "Documents" },
  // Capture ↔ Media Library unify (Phase 1): a SOURCE filter (orthogonal to the
  // type tabs). Shows only items the BE tagged `source==="capture"`. Shared by the
  // Library page AND the in-builder reuse picker, so a captured item is findable —
  // and insertable into a Segment — from either surface.
  { key: "captures", label: "Captures" },
];

// Singular type label for a media item (list "Type" column + a11y).
export const TYPE_LABEL = {
  image: "Image",
  video: "Video",
  audio: "Audio",
  document: "Document",
};

export const VIEW_MODES = ["large", "small", "list"];

export const PER_PAGE_OPTIONS = [25, 50, 100];

// ── Formatting ────────────────────────────────────────────────────────────────

// Bytes → human string mirroring the deck ("2.4 MB", "640 KB", "0 MB").
// Deterministic, locale-free; guards NaN/negative to "0 MB".
export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "0 MB";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  const value = n / Math.pow(1024, i);
  // < 10 → one decimal (2.4 MB); >= 10 → whole (48 MB); KB/B whole.
  const decimals = i >= 2 && value < 10 ? 1 : 0;
  return `${value.toFixed(decimals)} ${units[i]}`;
}

// Clamp a raw 0..100(+) percentage to a safe CSS width string "42%".
// Accepts number or "42%" string; NaN/neg → 0, >100 → 100 (bar never overflows).
export function clampPercent(percent) {
  const raw = typeof percent === "string" ? parseFloat(percent.replace("%", "")) : Number(percent);
  if (!Number.isFinite(raw) || raw < 0) return 0;
  return Math.min(100, raw);
}

// Video duration seconds → "0:42" / "3:05" chip (null when not a finite number).
// #960: `Number(null) === 0` (finite, >=0), so a bare `Number(seconds)` conversion
// turned every item with no known duration (null/undefined — the live API sends
// neither, see mediaAdapter.js) into a lying "0:00" chip instead of omitting it.
// null/undefined/"" are explicitly "unknown", never coerced to zero.
export function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || seconds === "") return null;
  const s = Number(seconds);
  if (!Number.isFinite(s) || s < 0) return null;
  const m = Math.floor(s / 60);
  const rem = Math.floor(s % 60);
  return `${m}:${String(rem).padStart(2, "0")}`;
}

// ── EW-010: honest refcount / deletion ────────────────────────────────────────

// Usage state of a media asset, kept HONEST about the EW-010 gap:
//  - null/undefined  → 'unknown'  (BE refcount not shipped yet — WL-072)
//  - 0               → 'unused'
//  - > 0             → 'used'
// A null must NOT be shown as "unused" — that would be a dishonest claim the
// asset is safe to delete when we simply don't know.
export function usageState(usedInWheels) {
  if (usedInWheels === null || usedInWheels === undefined || usedInWheels === "") return "unknown";
  const n = Number(usedInWheels);
  if (!Number.isFinite(n)) return "unknown";
  return n > 0 ? "used" : "unused";
}

// Human refcount label. unknown → "Usage not tracked yet"; 0 → "Not used yet";
// N → "Used in N Wheel(s)".
export function refcountLabel(usedInWheels) {
  const state = usageState(usedInWheels);
  if (state === "unknown") return "Usage not tracked yet";
  if (state === "unused") return "Not used yet";
  const n = Number(usedInWheels);
  return `Used in ${n} ${n === 1 ? "Wheel" : "Wheels"}`;
}

// A deletion is DESTRUCTIVE only when the asset is a CONFIRMED reference
// (refcount > 0) — the "removes it from every Wheel that uses it" case.
// EW-1283: an UNKNOWN refcount (null/undefined — BE refcount WL-072 not shipped)
// must NOT be reported as "still in use". Unknown is neither destructive nor a
// safe "unused" delete — the modal surfaces it as its own honest "usage not
// tracked yet" caution (see DeleteMediaModal), never a false in-use claim.
export function isDeletionDestructive(usedInWheels) {
  return usageState(usedInWheels) === "used";
}

// ── Media Library delete-modal-ux cluster: FE-side profile-picture guard ──────
// The BE's delete-guard 409s already cover wheel wallpaper/image/preview/poster
// and the avatar itself (users/views.py) — but that guard only fires AFTER the
// user clicks Delete and the request round-trips. Gordon's report: the modal
// should know a file is the user's own profile picture and gray out Delete the
// MOMENT it opens, not after a failed attempt. This is a client-side UX guard
// only — the BE guard remains the source of truth for anything this can't see.

// Normalise a URL/storage-path down to a comparable S3 key. The two S3 URL
// shapes the BE hands out for the SAME object put the bucket in DIFFERENT
// places — virtual-hosted-style (https://bucket.s3.amazonaws.com/key) has it
// in the HOST, path-style (https://s3.amazonaws.com/bucket/key) has it as the
// FIRST path segment — so a plain "everything after the host" slice does NOT
// land on the same suffix for both (see media-delete-guard-path-style-url,
// PR#2857, which hit the same shape mismatch BE-side). Anchor on "uploads/"
// instead — every storage key in this app is built as
// `uploads/<user_id>/...` (getUserProfilePath/getWheelUploadPath/
// getBusinessBoxPath, utils/fileUpload.js), so it's present verbatim in every
// shape and unaffected by where the bucket sits.
function storageKeyOf(urlOrPath) {
  if (!urlOrPath || typeof urlOrPath !== "string") return null;
  const noQuery = urlOrPath.split("?")[0];
  const noProtocol = noQuery.replace(/^https?:\/\//i, "");
  const uploadsIdx = noProtocol.toLowerCase().indexOf("uploads/");
  let pathname;
  if (uploadsIdx !== -1) {
    pathname = noProtocol.slice(uploadsIdx);
  } else {
    const firstSlash = noProtocol.indexOf("/");
    pathname = firstSlash === -1 ? noProtocol : noProtocol.slice(firstSlash + 1);
  }
  const key = decodeURIComponent(pathname).replace(/^\/+/, "").toLowerCase();
  return key || null;
}

// Is this media item the current user's profile picture? Compares by S3 key
// (not raw string equality) so it survives the two S3 URL shapes the BE hands
// out interchangeably.
export function isProfilePicture(item, profileImageUrl) {
  if (!item || !profileImageUrl) return false;
  const itemKey = storageKeyOf(item.path) || storageKeyOf(item.url);
  const profileKey = storageKeyOf(profileImageUrl);
  return Boolean(itemKey && profileKey && itemKey === profileKey);
}

// One item's hard delete-block reason, computed from props alone (no click,
// no request) so the caller can render it the instant the modal opens.
// null = not blocked. Today the only known client-side-detectable reason is
// "profile-picture"; anything else (wheel references, business box, …) is
// still only known once the BE's 409 comes back.
export function blockReason(item, profileImageUrl) {
  if (isProfilePicture(item, profileImageUrl)) return "profile-picture";
  return null;
}

// ── Filter / sort / paginate (pure, drive the grid + list identically) ─────────

// Filter by active tab + a case-insensitive search substring. Never mutates input.
//
// Capture ↔ Media Library unify (Phase 1):
//  - the "captures" tab is a SOURCE filter (item.source === "capture"), orthogonal
//    to the type tabs; every other tab keeps its type semantics unchanged.
//  - search now also matches the capture caption + capture type, not just filename,
//    so a captured note is findable by its text — the Library search was
//    filename-only before (captures had no discoverable text).
export function filterMedia(items, { activeType = "all", query = "" } = {}) {
  const q = query.trim().toLowerCase();
  return (items || []).filter((it) => {
    const typeOk =
      activeType === "all" ||
      (activeType === "captures"
        ? it.source === "capture"
        : `${it.type}s` === activeType || it.type === activeType);
    const haystack = [it.name, it.caption, it.captureType]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const searchOk = !q || haystack.includes(q);
    return typeOk && searchOk;
  });
}

const SORTERS = {
  newest: (a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0),
  oldest: (a, b) => new Date(a.uploadedAt || 0) - new Date(b.uploadedAt || 0),
  name: (a, b) => (a.name || "").localeCompare(b.name || ""),
  size: (a, b) => Number(b.size || 0) - Number(a.size || 0),
  type: (a, b) => (a.type || "").localeCompare(b.type || ""),
};

export function sortMedia(items, sortKey = "newest") {
  const fn = SORTERS[sortKey] || SORTERS.newest;
  return [...(items || [])].sort(fn);
}

// List-view column header → sortMedia key (ML-vY-02: the header row rendered
// as inert spans with no key of its own; a click needs to know which SORTERS
// entry its column corresponds to). "Uploaded" reuses "newest" — there's no
// separate ascending/descending toggle, matching the toolbar's single-direction
// per-key sorts.
export const COLUMN_SORT_KEYS = {
  name: "name",
  type: "type",
  size: "size",
  uploaded: "newest",
};

// Total page count for a footer pager (never < 1 so "Page 1 of 1" stays honest).
export function pageCount(total, perPage) {
  const t = Math.max(0, Number(total) || 0);
  const p = Math.max(1, Number(perPage) || 1);
  return Math.max(1, Math.ceil(t / p));
}

// Slice the page window for a given 1-indexed page.
export function paginate(items, page = 1, perPage = 25) {
  const p = Math.max(1, Number(page) || 1);
  const size = Math.max(1, Number(perPage) || 1);
  const start = (p - 1) * size;
  return (items || []).slice(start, start + size);
}
